// True redaction: pages that carry redactions are replaced by a picture of the page with the marked areas blacked out, and the file is
// rewritten from the objects still reachable from the catalog — the old text, images and drawing commands are not hidden, they are gone.

import { PdfDocument } from "./document";
import { Dict, PName, PRef, Stream, type PObj } from "./parse";

export interface RedactedPage {
    /** 0-based page index. */
    page: number;
    /** A JPEG of the whole page, black boxes included. */
    jpeg: Uint8Array;
    /** Its size in pixels. */
    width: number;
    height: number;
}

export interface RewriteOptions {
    /** Also drop the document information, XMP metadata and attachments. */
    scrubMetadata?: boolean;
}

const enc = new TextEncoder();
const num = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(4).replace(/0+$/, "").replace(/\.$/, ""));

class Out {
    private parts: Uint8Array[] = [];
    length = 0;
    push(b: Uint8Array) { this.parts.push(b); this.length += b.length; }
    text(s: string) { this.push(enc.encode(s)); }
    bytes(): Uint8Array { const o = new Uint8Array(this.length); let p = 0; for (const x of this.parts) { o.set(x, p); p += x.length; } return o; }
}

/** Rewrites `original` with the given pages replaced by pictures and only reachable objects kept. */
export function redactPages(original: Uint8Array, pages: RedactedPage[], opts: RewriteOptions = {}): Uint8Array {
    const doc = PdfDocument.open(original);
    const xref = doc.xref;
    if (xref.trailer.has("Encrypt")) throw new Error("This PDF is encrypted, so it can't be redacted.");
    doc.pages();
    const refs = doc.pageRefs;
    const replaced = new Map<number, RedactedPage>();
    for (const p of pages) { const r = refs[p.page]; if (r) replaced.set(r.num, p); }

    const out = new Out();
    out.text("%PDF-1.7\n%\xe2\xe3\xcf\xd3\n");
    const offsets = new Map<number, number>();
    const map = new Map<number, number>();
    const queue: Array<{ old: number; gen: number }> = [];
    let next = 1;
    const target = (r: PRef): number => {
        let n = map.get(r.num);
        if (n === undefined) { n = next++; map.set(r.num, n); queue.push({ old: r.num, gen: r.gen }); }
        return n;
    };
    // new objects (the page pictures) written after the traversal
    const extra: Array<{ num: number; head: string; data: Uint8Array }> = [];

    const w = (o: PObj | undefined): string => {
        if (o === undefined || o === null) return "null";
        if (typeof o === "boolean") return o ? "true" : "false";
        if (typeof o === "number") return num(o);
        if (typeof o === "string") { let h = "<"; for (let i = 0; i < o.length; i++) h += o.charCodeAt(i).toString(16).padStart(2, "0"); return h + ">"; }
        if (o instanceof PName) return "/" + [...o.name].map((c) => { const k = c.charCodeAt(0); return k < 33 || k > 126 || "#()<>[]{}/%".includes(c) ? "#" + k.toString(16).padStart(2, "0") : c; }).join("");
        if (o instanceof PRef) return `${target(o)} 0 R`;
        if (Array.isArray(o)) return "[" + o.map(w).join(" ") + "]";
        if (o instanceof Dict) { let s = "<<"; for (const k of o.keys()) s += w(new PName(k)) + " " + w(o.raw(k)) + " "; return s + ">>"; }
        return "null";
    };

    const writeObj = (n: number, head: string, data?: Uint8Array) => {
        offsets.set(n, out.length);
        if (data) { out.text(`${n} 0 obj\n${head}\nstream\n`); out.push(data); out.text("\nendstream\nendobj\n"); }
        else out.text(`${n} 0 obj\n${head}\nendobj\n`);
    };

    // catalog (and the information dictionary)
    const rootRef = xref.trailer.raw("Root");
    if (!(rootRef instanceof PRef)) throw new Error("The PDF has no catalog.");
    const rootNum = target(rootRef);
    const infoRef = xref.trailer.raw("Info");
    let infoNum = 0;
    if (infoRef instanceof PRef && !opts.scrubMetadata) infoNum = target(infoRef);

    while (queue.length) {
        const { old, gen } = queue.shift()!;
        const n = map.get(old)!;
        const obj = xref.fetch(new PRef(old, gen));
        if (old === rootRef.num && obj instanceof Dict) {
            const c = new Dict(xref);
            for (const k of obj.keys()) {
                // forms would keep the typed values of removed pages; the picture pages carry no widgets
                if (k === "AcroForm" && replaced.size) continue;
                if (opts.scrubMetadata && (k === "Metadata" || k === "Names" || k === "PieceInfo" || k === "StructTreeRoot" || k === "MarkInfo")) continue;
                c.set(k, obj.raw(k)!);
            }
            writeObj(n, w(c));
            continue;
        }
        const rp = replaced.get(old);
        if (rp && obj instanceof Dict) {
            const c = new Dict(xref);
            for (const k of obj.keys()) if (!["Contents", "Resources", "Annots", "Rotate", "CropBox", "MediaBox", "TrimBox", "BleedBox", "ArtBox", "Thumb", "B", "PieceInfo", "AA", "Metadata", "StructParents", "Group"].includes(k)) c.set(k, obj.raw(k)!);
            // the picture is the page as displayed (rotation applied), so the new page is that size and unrotated
            const [pw, ph] = doc.page(rp.page).size;
            const imgNum = next++, contentNum = next++;
            const q = enc.encode(`q ${num(pw)} 0 0 ${num(ph)} 0 0 cm /Im1 Do Q
`);
            c.set("MediaBox", [0, 0, pw, ph]);
            extra.push({ num: imgNum, head: `<< /Type /XObject /Subtype /Image /Width ${rp.width} /Height ${rp.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${rp.jpeg.length} >>`, data: rp.jpeg });
            extra.push({ num: contentNum, head: `<< /Length ${q.length} >>`, data: q });
            // the new objects are numbered already, so they are written straight into the dictionary
            writeObj(n, w(c).slice(0, -2) + `/Contents ${contentNum} 0 R /Resources << /XObject << /Im1 ${imgNum} 0 R >> >> >>`);
            continue;
        }
        if (obj instanceof Stream) {
            const d = new Dict(xref);
            for (const k of obj.dict.keys()) if (k !== "Length") d.set(k, obj.dict.raw(k)!);
            writeObj(n, w(d).slice(0, -2) + `/Length ${obj.raw.length} >>`, obj.raw);
        } else writeObj(n, w(obj));
    }
    for (const e of extra.splice(0)) writeObj(e.num, e.head, e.data);

    const size = next;
    const startxref = out.length;
    let x = `xref\n0 ${size}\n0000000000 65535 f \n`;
    for (let i = 1; i < size; i++) x += `${String(offsets.get(i) ?? 0).padStart(10, "0")} 00000 ${offsets.has(i) ? "n" : "f"} \n`;
    out.text(x + `trailer\n<< /Size ${size} /Root ${rootNum} 0 R ${infoNum ? `/Info ${infoNum} 0 R ` : ""}>>\nstartxref\n${startxref}\n%%EOF\n`);
    return out.bytes();
}
